import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Check, X, Clock, Mail, User, Package, Zap, Settings, ChevronDown, ChevronUp, Eye, Plus, Edit, AlertTriangle, Copy } from 'lucide-react';
import { adminAPI, panelRequestAPI, solarAPI } from '../../services/api';
import { PanelRequest, SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Input } from '../common/Input';

export const PanelRequests: React.FC = () => {
  const [requests, setRequests] = useState<PanelRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<PanelRequest | null>(null);
  const [panelFormData, setPanelFormData] = useState<any>({});
  const [createPanelLoading, setCreatePanelLoading] = useState(false);
  const [validationResults, setValidationResults] = useState<{ [key: string]: { isValid: boolean; missingFields: string[]; warnings: string[] } }>({});
  const [editingRequest, setEditingRequest] = useState<PanelRequest | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPanelRequests();
      setRequests(data);
      
      // Validate each request
      const validations: { [key: string]: { isValid: boolean; missingFields: string[]; warnings: string[] } } = {};
      for (const request of data) {
        try {
          const validation = await adminAPI.validatePanelRequest(request.id);
          validations[request.id] = validation;
        } catch (err) {
          console.error(`Failed to validate request ${request.id}:`, err);
        }
      }
      setValidationResults(validations);
    } catch (err: any) {
      setError('Failed to load panel requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: string, adminNotes?: string) => {
    try {
      // Check validation before approval
      if (status === 'approved' && validationResults[id] && !validationResults[id].isValid) {
        setError(`Cannot approve request: Missing critical fields - ${validationResults[id].missingFields.join(', ')}`);
        return;
      }
      
      await adminAPI.updatePanelRequestStatus(id, status, adminNotes);
      setSuccess(`Panel request ${status} successfully`);
      loadRequests();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update panel request status');
    }
  };

  const handleViewAttachment = (request: PanelRequest) => {
    try {
      // Use the same base URL as the API
      const baseUrl = 'https://api.solarlookup.com';
      
      // Use the new attachmentUrl if available, otherwise construct from filename
      let fileUrl;
      if (request.attachmentUrl) {
        fileUrl = `${baseUrl}${request.attachmentUrl}`;
      } else if (request.attachmentPath) {
        // Extract filename from full path for legacy requests
        const filename = request.attachmentPath.split('/').pop() || request.attachmentPath.split('\\').pop();
        fileUrl = `${baseUrl}/api/uploads/${filename}`;
      } else {
        setError('No attachment available');
        return;
      }
      
      window.open(fileUrl, '_blank');
    } catch (err) {
      setError('Failed to load attachment');
    }
  };

  const handleCreatePanel = (request: PanelRequest) => {
    setSelectedRequest(request);
    const data = request.panelData?.panelData || {};
    
    // Map the request data to SolarPanel fields
    const mappedData = {
      SKU: `${data.brand || ''}-${data.model || ''}`.replace(/\s+/g, '-'),
      Description: data.description || `${data.brand || ''} ${data.model || ''}`.trim(),
      Watts_W: data.wattage || data.Watts_W || 0,
      CellType: data.technology || data.CellType || '',
      NumberOfCells_count: data.numberOfCells || data.cells || data.NumberOfCells_count || 0,
      Length_mm: data.length || data.Length_mm || 0,
      Width_mm: data.width || data.Width_mm || 0,
      Thickness_mm: data.thickness || data.Thickness_mm || 0,
      Weight_kg: data.weight || data.Weight_kg || 0,
      PanelsPerPallet_count: data.panelsPerPallet || data.PanelsPerPallet_count || 0,
      PalletsPerTruck_count: data.palletsPerTruck || data.PalletsPerTruck_count || 0,
      SnowLoad_Pa: data.snowLoad || data.SnowLoad_Pa || 0,
      WindLoad_Pa: data.windLoad || data.WindLoad_Pa || 0,
      ConnectorType: data.connectorType || data.ConnectorType || '',
      CableLength_mm: data.cableLength || data.CableLength_mm || 0,
      Pmax_W: data.pmax || data.Pmax_W || data.wattage || 0,
      Vmp_V: data.vmp || data.Vmp_V || 0,
      Imp_A: data.imp || data.Imp_A || 0,
      Voc_V: data.voc || data.Voc_V || 0,
      Isc_A: data.isc || data.Isc_A || 0,
      ModuleEfficiency_percent: data.efficiency || data.ModuleEfficiency_percent || 0,
      TempCoeffPmax_percent_per_C: data.tempCoeffPmax || data.TempCoeffPmax_percent_per_C || 0,
      TempCoeffVoc_percent_per_C: data.tempCoeffVoc || data.TempCoeffVoc_percent_per_C || 0,
      TempCoeffIsc_percent_per_C: data.tempCoeffIsc || data.TempCoeffIsc_percent_per_C || 0,
      remarks: data.remarks || data.description || ''
    };
    
    setPanelFormData(mappedData);
    setShowCreatePanel(true);
  };

  const handlePanelFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPanelFormData((prev: any) => ({
      ...prev,
      [name]: ['Watts_W', 'NumberOfCells_count', 'Length_mm', 'Width_mm', 'Thickness_mm', 'Weight_kg', 
               'PanelsPerPallet_count', 'PalletsPerTruck_count', 'SnowLoad_Pa', 'WindLoad_Pa', 'CableLength_mm',
               'Pmax_W', 'Vmp_V', 'Imp_A', 'Voc_V', 'Isc_A', 'ModuleEfficiency_percent', 
               'TempCoeffPmax_percent_per_C', 'TempCoeffVoc_percent_per_C', 'TempCoeffIsc_percent_per_C'].includes(name)
        ? Number(value) || 0
        : value
    }));
  };

  const handleEditRequest = (request: PanelRequest) => {
    setEditingRequest(request);
    const data = request.panelData?.panelData || {};
    setEditFormData({
      brand: data.brand || '',
      model: data.model || '',
      description: data.description || '',
      wattage: data.wattage || 0,
      efficiency: data.efficiency || 0,
      technology: data.technology || '',
      pmax: data.pmax || 0,
      vmp: data.vmp || 0,
      imp: data.imp || 0,
      voc: data.voc || 0,
      isc: data.isc || 0,
      length: data.length || 0,
      width: data.width || 0,
      thickness: data.thickness || 0,
      weight: data.weight || 0,
      numberOfCells: data.numberOfCells || 0,
      frameColor: data.frameColor || '',
      tempCoeffPmax: data.tempCoeffPmax || 0,
      tempCoeffVoc: data.tempCoeffVoc || 0,
      tempCoeffIsc: data.tempCoeffIsc || 0,
      panelsPerPallet: data.panelsPerPallet || 0,
      palletsPerTruck: data.palletsPerTruck || 0,
      snowLoad: data.snowLoad || 0,
      windLoad: data.windLoad || 0,
      warranty: data.warranty || 0,
      cableLength: data.cableLength || 0,
      connectorType: data.connectorType || '',
      remarks: data.remarks || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRequest) return;

    try {
      // Update the panel data in the request
      const updatedRequest = {
        ...editingRequest,
        panelData: {
          ...editingRequest.panelData,
          panelData: editFormData
        }
      };

      // Here you would call an API to update the request
      // For now, we'll simulate it by updating the local state
      setRequests((prev: any) => prev.map((req: any) => 
        req.id === editingRequest.id ? updatedRequest : req
      ));

      setSuccess('Panel request updated successfully');
      setEditingRequest(null);
      setEditFormData({});
      
      // Refresh validation for the updated request
      try {
        const validation = await adminAPI.validatePanelRequest(editingRequest.id);
        setValidationResults(prev => ({
          ...prev,
          [editingRequest.id]: validation
        }));
      } catch (err) {
        console.error('Failed to revalidate after edit:', err);
      }
      
    } catch (err: any) {
      setError('Failed to update panel request');
    }
  };

  const handleSubmitPanel = async () => {
    if (!selectedRequest) return;
    
    setCreatePanelLoading(true);
    try {
      // Create the panel
      await solarAPI.create(panelFormData);
      
      // Update the request status to approved
      await adminAPI.updatePanelRequestStatus(
        selectedRequest.id, 
        'approved', 
        'Panel created and added to database'
      );
      
      setSuccess('Panel created successfully and request approved!');
      setShowCreatePanel(false);
      setSelectedRequest(null);
      setPanelFormData({});
      loadRequests();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create panel');
    } finally {
      setCreatePanelLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
        return <Check className="h-4 w-4" />;
      case 'rejected':
        return <X className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const renderPanelDataCard = (data: any, supplierRequester?: any) => {
    const contactInfo = {
      email: data.email || supplierRequester?.email,
      name: data.name,
      phone: data.phone,
      company: data.company
    };

    const panelSpecs = {
      brand: data.brand,
      model: data.model,
      wattage: data.wattage,
      efficiency: data.efficiency,
      technology: data.technology
    };

    const physicalSpecs = {
      dimensions: data.dimensions,
      weight: data.weight,
      cells: data.cells || data.numberOfCells,
      length: data.length,
      width: data.width,
      thickness: data.thickness,
      frameColor: data.frameColor
    };

    const electricalSpecs = {
      vmp: data.vmp,
      imp: data.imp,
      voc: data.voc,
      isc: data.isc,
      pmax: data.pmax
    };

    return (
      <div className="space-y-6">
        {/* Contact Information */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <User className="h-4 w-4 text-blue-600 mr-2" />
            <h4 className="font-medium text-blue-900">Contact Information</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {contactInfo.email && (
              <div className="flex items-center">
                <Mail className="h-3 w-3 text-blue-500 mr-2" />
                <span className="font-medium">Email:</span>
                <span className="ml-1">{contactInfo.email}</span>
              </div>
            )}
            {contactInfo.name && (
              <div>
                <span className="font-medium">Name:</span>
                <span className="ml-1">{contactInfo.name}</span>
              </div>
            )}
            {contactInfo.phone && (
              <div>
                <span className="font-medium">Phone:</span>
                <span className="ml-1">{contactInfo.phone}</span>
              </div>
            )}
            {contactInfo.company && (
              <div>
                <span className="font-medium">Company:</span>
                <span className="ml-1">{contactInfo.company}</span>
              </div>
            )}
          </div>
        </div>

        {/* Panel Specifications */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <Package className="h-4 w-4 text-green-600 mr-2" />
            <h4 className="font-medium text-green-900">Panel Specifications</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {Object.entries(panelSpecs).filter(([_, value]) => value).map(([key, value]) => (
              <div key={key}>
                <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                <span className="ml-1">{value}{key === 'wattage' ? 'W' : key === 'efficiency' ? '%' : ''}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Physical Specifications */}
        {Object.values(physicalSpecs).some(v => v) && (
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Settings className="h-4 w-4 text-purple-600 mr-2" />
              <h4 className="font-medium text-purple-900">Physical Specifications</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              {Object.entries(physicalSpecs).filter(([_, value]) => value).map(([key, value]) => (
                <div key={key}>
                  <span className="font-medium capitalize">{key}:</span>
                  <span className="ml-1">{value}{key === 'weight' ? ' kg' : key.includes('length') || key.includes('width') || key.includes('thickness') ? ' mm' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Electrical Specifications */}
        {Object.values(electricalSpecs).some(v => v) && (
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Zap className="h-4 w-4 text-yellow-600 mr-2" />
              <h4 className="font-medium text-yellow-900">Electrical Specifications</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {Object.entries(electricalSpecs).filter(([_, value]) => value).map(([key, value]) => (
                <div key={key}>
                  <span className="font-medium uppercase">{key}:</span>
                  <span className="ml-1">{value}{key.includes('v') ? 'V' : key.includes('i') ? 'A' : 'W'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        {(data.description || data.urgency || data.remarks) && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
            <div className="space-y-2 text-sm">
              {data.description && (
                <div>
                  <span className="font-medium">Description:</span>
                  <p className="mt-1 text-gray-700">{data.description}</p>
                </div>
              )}
              {data.remarks && (
                <div>
                  <span className="font-medium">Remarks:</span>
                  <p className="mt-1 text-gray-700">{data.remarks}</p>
                </div>
              )}
              {data.urgency && (
                <div>
                  <span className="font-medium">Urgency:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    data.urgency === 'high' ? 'bg-red-100 text-red-800' :
                    data.urgency === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {data.urgency.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logistics Information */}
        {(data.panelsPerPallet || data.palletsPerTruck || data.snowLoad || data.windLoad) && (
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-3">Logistics & Load Specifications</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              {data.panelsPerPallet && (
                <div>
                  <span className="font-medium">Panels per Pallet:</span>
                  <span className="ml-1">{data.panelsPerPallet}</span>
                </div>
              )}
              {data.palletsPerTruck && (
                <div>
                  <span className="font-medium">Pallets per Truck:</span>
                  <span className="ml-1">{data.palletsPerTruck}</span>
                </div>
              )}
              {data.snowLoad && (
                <div>
                  <span className="font-medium">Snow Load:</span>
                  <span className="ml-1">{data.snowLoad} Pa</span>
                </div>
              )}
              {data.windLoad && (
                <div>
                  <span className="font-medium">Wind Load:</span>
                  <span className="ml-1">{data.windLoad} Pa</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Temperature Coefficients */}
        {(data.tempCoeffPmax || data.tempCoeffVoc || data.tempCoeffIsc) && (
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="font-medium text-purple-900 mb-3">Temperature Coefficients</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {data.tempCoeffPmax && (
                <div>
                  <span className="font-medium">Pmax:</span>
                  <span className="ml-1">{data.tempCoeffPmax}%/°C</span>
                </div>
              )}
              {data.tempCoeffVoc && (
                <div>
                  <span className="font-medium">Voc:</span>
                  <span className="ml-1">{data.tempCoeffVoc}%/°C</span>
                </div>
              )}
              {data.tempCoeffIsc && (
                <div>
                  <span className="font-medium">Isc:</span>
                  <span className="ml-1">{data.tempCoeffIsc}%/°C</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900">Panel Requests</h1>
      </motion.div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-[#63B56F] mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Panel Requests</h2>
            <span className="ml-auto bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
              {requests.length} total
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {requests.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No panel requests found</p>
            </div>
          ) : (
            requests.map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{request.status.toUpperCase()}</span>
                      </span>
                      <span className="ml-3 text-sm text-gray-500">
                        {new Date(request?.created_at).toLocaleDateString()}
                      </span>
                      <span className="ml-3 text-sm text-gray-500">
                        {request.requestedByType === 'supplier' ? 'Supplier' : 'User'}
                      </span>
                      
                      {/* Validation Status */}
                      {validationResults[request.id] && (
                        <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          validationResults[request.id].isValid 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {validationResults[request.id].isValid ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Complete
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {validationResults[request.id].missingFields.length} Missing Fields
                            </>
                          )}
                        </span>
                      )}
                      
                      {/* Duplicate Status */}
                      {request.hasDuplicates && (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <Copy className="w-3 h-3 mr-1" />
                          Duplicate Found
                        </span>
                      )}
                    </div>

                    {/* Quick Summary */}
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900">{request.requestedByType === "supplier" ? request.supplierRequester?.email : request.email}</span>
                        {request.panelData?.panelData?.name && (
                          <span className="ml-2 text-gray-600">({request.panelData.panelData.name})</span>
                        )}
                      </div>
                      {(request.panelData?.panelData?.brand || request.panelData?.panelData?.model) && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Package className="h-4 w-4 text-gray-400 mr-2" />
                          <span>
                            {request.panelData?.panelData?.brand && request.panelData?.panelData?.model 
                              ? `${request.panelData.panelData.brand} ${request.panelData.panelData.model}`
                              : request.panelData?.panelData?.brand || request.panelData?.panelData?.model || 'Panel Request'
                            }
                            {request.panelData?.panelData?.wattage && ` - ${request.panelData.panelData.wattage}W`}
                          </span>
                        </div>
                      )}

                      {request.supplierRequester && (
                        <div className="mt-2 text-sm text-gray-500">
                          <span className="font-medium">Supplier:</span>
                          <span className="ml-1">{request.supplierRequester?.companyName || request.supplierRequester.email}</span>
                        </div>
                      )}
                    </div>

                    {/* Expandable Details */}
                    {expandedRequest === request.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-4 space-y-4"
                      >
                        {/* Validation Details */}
                        {validationResults[request.id] && (
                          <div className={`p-4 rounded-lg ${
                            validationResults[request.id].isValid 
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-red-50 border border-red-200'
                          }`}>
                            <div className="flex items-center mb-2">
                              {validationResults[request.id].isValid ? (
                                <>
                                  <Check className="w-5 h-5 text-green-600 mr-2" />
                                  <h4 className="font-medium text-green-900">All Critical Fields Complete</h4>
                                </>
                              ) : (
                                <>
                                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                                  <h4 className="font-medium text-red-900">Missing Critical Fields</h4>
                                </>
                              )}
                            </div>
                            
                            {!validationResults[request.id].isValid && (
                              <div className="text-sm text-red-700 mb-2">
                                <strong>Missing fields:</strong> {validationResults[request.id].missingFields.join(', ')}
                              </div>
                            )}
                            
                            {validationResults[request.id].warnings.length > 0 && (
                              <div className="text-sm text-yellow-700">
                                <strong>Warnings:</strong> {validationResults[request.id].warnings.join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Duplicate Information */}
                        {request.hasDuplicates && request.duplicateInfo && (
                          <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                            <div className="flex items-center mb-3">
                              <Copy className="w-5 h-5 text-orange-600 mr-2" />
                              <h4 className="font-medium text-orange-900">Duplicate Panel Detected</h4>
                            </div>
                            <p className="text-sm text-orange-700 mb-3">
                              Similar panel(s) already exist in the database:
                            </p>
                            <div className="space-y-2">
                              {request.duplicateInfo.map((duplicate: any, index: number) => (
                                <div key={index} className="bg-white rounded-md p-3 border border-orange-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="font-medium text-gray-900">
                                        {duplicate.Description || `${duplicate.SKU}`}
                                      </span>
                                      {duplicate.Watts_W && (
                                        <span className="ml-2 text-sm text-gray-600">
                                          ({duplicate.Watts_W}W)
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {duplicate.ModuleEfficiency_percent && `${duplicate.ModuleEfficiency_percent}% efficiency`}
                                      {duplicate.CellType && ` • ${duplicate.CellType}`}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {renderPanelDataCard(request.panelData?.panelData || {}, request.supplierRequester)}
                      </motion.div>
                    )}

                    {request.adminNotes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-1">Admin Notes:</h5>
                        <p className="text-sm text-gray-700">{request.adminNotes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setExpandedRequest(
                        expandedRequest === request.id ? null : request.id
                      )}
                    >
                      {expandedRequest === request.id ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          Details
                        </>
                      )}
                    </Button>

                    {request.attachmentPath && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewAttachment(request)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View File
                      </Button>
                    )}

                    {request.status === 'pending' && (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleEditRequest(request)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleCreatePanel(request)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Create Panel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(request.id, 'approved')}
                          disabled={validationResults[request.id] && !validationResults[request.id].isValid}
                          className={validationResults[request.id] && !validationResults[request.id].isValid ? 'opacity-50 cursor-not-allowed' : ''}
                          title={
                            validationResults[request.id] && !validationResults[request.id].isValid 
                              ? `Cannot approve: Missing ${validationResults[request.id].missingFields.join(', ')}` 
                              : 'Approve request'
                          }
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleUpdateStatus(request.id, 'rejected', 'Request rejected by admin')}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Create Panel Modal */}
      {showCreatePanel && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Create Panel from Request</h3>
                <button
                  onClick={() => setShowCreatePanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">SKU</label>
                  <Input
                    name="SKU"
                    value={panelFormData.SKU}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <Input
                    name="Description"
                    value={panelFormData.Description}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Wattage (W)</label>
                  <Input
                    type="number"
                    name="Watts_W"
                    value={panelFormData.Watts_W}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cell Type</label>
                  <Input
                    name="CellType"
                    value={panelFormData.CellType}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Number of Cells</label>
                  <Input
                    type="number"
                    name="NumberOfCells_count"
                    value={panelFormData.NumberOfCells_count}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Length (mm)</label>
                  <Input
                    type="number"
                    name="Length_mm"
                    value={panelFormData.Length_mm}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Width (mm)</label>
                  <Input
                    type="number"
                    name="Width_mm"
                    value={panelFormData.Width_mm}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Thickness (mm)</label>
                  <Input
                    type="number"
                    name="Thickness_mm"
                    value={panelFormData.Thickness_mm}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Weight (kg)</label>
                  <Input
                    type="number"
                    name="Weight_kg"
                    value={panelFormData.Weight_kg}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Panels Per Pallet</label>
                  <Input
                    type="number"
                    name="PanelsPerPallet_count"
                    value={panelFormData.PanelsPerPallet_count}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pallets Per Truck</label>
                  <Input
                    type="number"
                    name="PalletsPerTruck_count"
                    value={panelFormData.PalletsPerTruck_count}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Snow Load (Pa)</label>
                  <Input
                    type="number"
                    name="SnowLoad_Pa"
                    value={panelFormData.SnowLoad_Pa}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Wind Load (Pa)</label>
                  <Input
                    type="number"
                    name="WindLoad_Pa"
                    value={panelFormData.WindLoad_Pa}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Connector Type</label>
                  <Input
                    name="ConnectorType"
                    value={panelFormData.ConnectorType}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cable Length (mm)</label>
                  <Input
                    type="number"
                    name="CableLength_mm"
                    value={panelFormData.CableLength_mm}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pmax (W)</label>
                  <Input
                    type="number"
                    name="Pmax_W"
                    value={panelFormData.Pmax_W}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vmp (V)</label>
                  <Input
                    type="number"
                    name="Vmp_V"
                    value={panelFormData.Vmp_V}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Imp (A)</label>
                  <Input
                    type="number"
                    name="Imp_A"
                    value={panelFormData.Imp_A}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Voc (V)</label>
                  <Input
                    type="number"
                    name="Voc_V"
                    value={panelFormData.Voc_V}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Isc (A)</label>
                  <Input
                    type="number"
                    name="Isc_A"
                    value={panelFormData.Isc_A}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Module Efficiency (%)</label>
                  <Input
                    type="number"
                    name="ModuleEfficiency_percent"
                    value={panelFormData.ModuleEfficiency_percent}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Temp Coeff Pmax (°C)</label>
                  <Input
                    type="number"
                    name="TempCoeffPmax_percent_per_C"
                    value={panelFormData.TempCoeffPmax_percent_per_C}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Temp Coeff Voc (°C)</label>
                  <Input
                    type="number"
                    name="TempCoeffVoc_percent_per_C"
                    value={panelFormData.TempCoeffVoc_percent_per_C}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Temp Coeff Isc (°C)</label>
                  <Input
                    type="number"
                    name="TempCoeffIsc_percent_per_C"
                    value={panelFormData.TempCoeffIsc_percent_per_C}
                    onChange={handlePanelFormChange}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Remarks</label>
                  <Input
                    name="remarks"
                    value={panelFormData.remarks}
                    onChange={handlePanelFormChange}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setShowCreatePanel(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitPanel}
                  loading={createPanelLoading}
                >
                  Create Panel & Approve Request
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Panel Request Modal */}
      {editingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Edit Panel Request</h3>
                <button
                  onClick={() => setEditingRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Brand *</label>
                  <Input
                    name="brand"
                    value={editFormData.brand}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, brand: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Model *</label>
                  <Input
                    name="model"
                    value={editFormData.model}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, model: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Wattage (W) *</label>
                  <Input
                    type="text"
                    name="wattage"
                    value={editFormData.wattage}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, wattage: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Efficiency (%)</label>
                  <Input
                    type="text"
                    name="efficiency"
                    value={editFormData.efficiency}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, efficiency: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Technology</label>
                  <Input
                    name="technology"
                    value={editFormData.technology}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, technology: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vmp (V)</label>
                  <Input
                    type="text"
                    name="vmp"
                    value={editFormData.vmp}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, vmp: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Imp (A)</label>
                  <Input
                    type="text"
                    name="imp"
                    value={editFormData.imp}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, imp: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Voc (V)</label>
                  <Input
                    type="text"
                    name="voc"
                    value={editFormData.voc}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, voc: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Isc (A)</label>
                  <Input
                    type="text"
                    name="isc"
                    value={editFormData.isc}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, isc: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Length (mm)</label>
                  <Input
                    type="text"
                    name="length"
                    value={editFormData.length}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, length: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Width (mm)</label>
                  <Input
                    type="text"
                    name="width"
                    value={editFormData.width}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, width: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Weight (kg)</label>
                  <Input
                    type="text"
                    name="weight"
                    value={editFormData.weight}
                    onChange={(e) => setEditFormData((prev: any) => ({ ...prev, weight: Number(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setEditingRequest(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
